import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import clsx from 'clsx';
import useStyles from './styles';
import { SimpleButton } from '../../index';
import { Filter } from '../../../types/main';
import { FilterMenusProps } from './FilterMenus';
import FilterLine from './Filters/FilterLine/FilterLine';
import useHotkey from '../../../hooks/useHotkey';

export type Props = FilterMenusProps & {
  menuFilterKeys: string[];
  filterLabels?: Filter[];
  leftContent: (filtersState: Filter[], setFiltersState: (filters: Filter[]) => void) => React.ReactNode;
  rightContent: (filtersState: Filter[], setFiltersState: (filters: Filter[]) => void) => React.ReactNode;
};

const FilterMenu = ({
  onApply,
  currentFilters,
  filterLabels,
  menuFilterKeys,
  leftContent,
  rightContent,
}: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const copyFilters = (filters: Filter[], filterKeys: string[]): Filter[] => {
    return [...filters
      .filter((f) => filterKeys.includes(f.key))
      .map((filter) => ({
        key: filter.key,
        label: filter.label,
        value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
      })),
    ];
  };
  const [menuFilters, setMenuFilters] = useState<Filter[]>(copyFilters(currentFilters, menuFilterKeys));

  const [otherFilters, setOtherFilters] = useState<Filter[]>(currentFilters
    .filter((f) => !menuFilterKeys.includes(f.key)));

  const applyHandler = useCallback(() => {
    const filters = [...menuFilters, ...otherFilters].map(({ key, value }) => [key, value]);
    onApply(filters);
    setOpen(false);
  }, [setOpen, onApply, menuFilters, otherFilters]);

  const resetHandler = useCallback(() => {
    const filters = [...otherFilters].map(({ key, value }) => [key, value]);
    setMenuFilters([]);
    onApply(filters);
    setOpen(false);
  }, [onApply, otherFilters, setMenuFilters]);

  const filterApplied: number = useMemo(() => {
    return menuFilters.reduce((count, filter) => {
      if (Array.isArray(filter.value)) {
        return count + filter.value.length;
      }
      return count + 1;
    }, 0);
  }, [menuFilters]);

  const handleClick = useCallback((event: any) => {

    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
    }

    if (!open) {
      setOpen(true);
    }
  }, [setOpen, setAnchorEl, anchorEl, open]);

  const handleClickAway = useCallback((e?: React.MouseEvent<Document>) => {
    if (open) {
      e?.stopPropagation();
      const newFilters = currentFilters.filter((f) => menuFilterKeys.includes(f.key));
      setMenuFilters(copyFilters(newFilters, menuFilterKeys));
      setOpen(false);
      e?.preventDefault();
    }
  }, [open, setOpen, setMenuFilters, currentFilters, menuFilterKeys]);

  useEffect(() => {
    setMenuFilters(copyFilters(currentFilters, menuFilterKeys));
    setOtherFilters(currentFilters.filter((f) => !menuFilterKeys.includes(f.key)));
  }, [setMenuFilters, setOtherFilters, currentFilters, menuFilterKeys]);

  useEffect(() => {
    if (open) {
      setMenuFilters([...currentFilters
        .filter((f) => menuFilterKeys.includes(f.key))
        .map((filter) => ({
          key: filter.key,
          label: filter.label,
          value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
        })),
      ]);
    }
  }, [open, setMenuFilters, currentFilters, menuFilterKeys]);

  useHotkey('esc', handleClickAway);

  // anchorEl

  return (
    <div className={classes.menuWrapper}>
      <div
        onClick={handleClick}
        className={clsx(classes.filterButton, {
          [classes.filterButtonActive]: open || filterApplied,
          [classes.filterButtonWithFilters]: filterApplied,
        })}
      >
        <FilterListIcon />
        {filterApplied > 0
        && (
          <div className={classes.counter}>{filterApplied}</div>
        )}
      </div>
      <Popper
        style={{ position: 'fixed' }}
        className={classes.popper}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        modifiers={{
          preventOverflow: {
            enabled: true,
            padding: {
              top: 60,
            },
            boundariesElement: 'scrollParent',
          },
        }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={(e) => handleClickAway(e)}>
            <Fade {...TransitionProps} timeout={200}>
              <Paper classes={{
                root: classes.paper,
              }}
              >
                <div className={classes.filterPanel}>
                  {/* filter */}
                  <div className={classes.header}>
                    <div>
                      <FilterLine
                        filters={menuFilters}
                        setFilters={setMenuFilters}
                        filterLabels={filterLabels}
                      />
                    </div>
                  </div>
                  {/* content */}
                  <div className={classes.content}>
                    {/* status */}
                    <div className={classes.leftContent}>
                      {leftContent(menuFilters, setMenuFilters)}
                    </div>
                    {/* updated */}
                    <div className={classes.rightContent}>
                      {rightContent(menuFilters, setMenuFilters)}
                    </div>
                  </div>
                  {/* controls */}
                  <div className={classes.bottom}>
                    <SimpleButton onClick={applyHandler} variant="contained">Apply</SimpleButton>
                    <SimpleButton onClick={resetHandler} variant="contained" colorType="default">Reset</SimpleButton>
                  </div>
                </div>
              </Paper>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </div>
  );
};

export default FilterMenu;
